import type { ProductVariation } from "@/lib/products";
import {
  DISKON_SLOT_COLUMN,
  DISKON_SLOT_KEY,
  DISKON_SLOTS,
  type DiskonSlot
} from "@/lib/reseller-groups";

// ─── Pengaturan diskon produk Non Web (mig 6360) ─────────────────────────────
//
// Produk Non Web (produk manual mig 121) hanya punya SATU varian ("Standar"),
// jadi "Pengaturan Diskon — default produk" di form Tambah Produk = diskon
// varian itu sendiri. Nilainya disimpan di kolom varian yang SAMA dengan produk
// katalog (`diskon_mode` + `diskon_reseller` … `diskon_slot15`), sehingga
// pricing.ts, keranjang admin, Buat Nota, dan re-snapshot membacanya tanpa
// cabang khusus.
//
// Semantik slot mengikuti pricing.ts: slot kosong / 0 = TANPA potongan khusus —
// pembeli grup itu mendapat diskon grupnya (`reseller_groups.discount_persen`).
// Aturan validasinya cermin guard RPC `admin_create_manual_product_order`
// (mig 6360); klien menolak lebih dulu supaya admin membaca pesannya di form.

export type ManualDiscountMode = "persen" | "nominal";

/** Plafon potongan PERSEN — tepat 100 sah (gratis), cermin pricing.ts. */
const MAX_DISKON_PERSEN = 100;

export type ManualDiscount = {
  mode: ManualDiscountMode;
  /** Potongan per slot grup (1..15). Slot absen / 0 = ikut diskon grup. */
  bySlot: Partial<Record<DiskonSlot, number>>;
};

/**
 * Bentuk kanonik: slot kosong / 0 dibuang; tanpa satu pun potongan → `null`
 * (mode tak bermakna tanpa potongan). Nilai negatif / bukan angka DIBIARKAN
 * supaya `validateManualDiscount` yang menolaknya, bukan hilang diam-diam.
 */
export function normalizeManualDiscount(
  d: ManualDiscount | null | undefined
): ManualDiscount | null {
  if (!d) return null;
  const bySlot: Partial<Record<DiskonSlot, number>> = {};
  let any = false;
  for (const slot of DISKON_SLOTS) {
    const v = d.bySlot[slot];
    if (v == null || v === 0) continue;
    bySlot[slot] = v;
    any = true;
  }
  return any ? { mode: d.mode, bySlot } : null;
}

/**
 * Satu isian diskon form → angka, menurut mode (bughunt D8-6):
 *  - PERSEN: desimal, koma atau titik ("12,5", "12.5", ",5", ".5").
 *  - NOMINAL: rupiah — titik = pemisah RIBUAN ("5.000" = 5000, "Rp 12.500").
 *    Dulu dibaca desimal: "5.000" jadi Rp 5, sementara impor membacanya
 *    Rp 5.000. Bentuk yang tak jelas ("5.5", "5.000,50") → NaN.
 * Teks yang bukan angka menjadi NaN — sengaja tidak dibuang, supaya
 * `validateManualDiscount` menolaknya dengan pesan. Minus dipertahankan
 * (ditolak validasi dengan pesan "negatif").
 */
export function parseManualDiscountInput(mode: ManualDiscountMode, raw: string): number {
  const text = raw.trim();
  if (mode === "persen") {
    // Number(".5") = 0.5, jadi bentuk tanpa angka depan tak perlu dilengkapi.
    return /^-?(\d+([.,]\d+)?|[.,]\d+)$/.test(text) ? Number(text.replace(",", ".")) : Number.NaN;
  }
  const rupiah = text.replace(/^rp\.?\s*/i, "").replace(/\s/g, "");
  if (/^-?\d+$/.test(rupiah)) return Number(rupiah);
  if (/^-?\d{1,3}(\.\d{3})+$/.test(rupiah)) return Number(rupiah.replace(/\./g, ""));
  return Number.NaN;
}

/**
 * Diskon dari isian form (teks mentah per slot). Isian kosong dilewati;
 * tafsir angkanya per mode — lihat `parseManualDiscountInput`.
 */
export function manualDiscountFromInputs(
  mode: ManualDiscountMode,
  inputs: Partial<Record<DiskonSlot, string>>
): ManualDiscount | null {
  const bySlot: Partial<Record<DiskonSlot, number>> = {};
  for (const slot of DISKON_SLOTS) {
    const raw = (inputs[slot] ?? "").trim();
    if (!raw) continue;
    bySlot[slot] = parseManualDiscountInput(mode, raw);
  }
  return normalizeManualDiscount({ mode, bySlot });
}

/**
 * Pesan galat pertama, atau null bila sah. `price` = Harga Jual (harga normal)
 * produknya; null = harga menyusul. `labelOf` memberi nama grup per slot untuk
 * pesan ("Diskon Agen …").
 */
export function validateManualDiscount(
  d: ManualDiscount | null | undefined,
  price: number | null,
  labelOf: (slot: DiskonSlot) => string = (slot) => `slot ${slot}`
): string | null {
  const n = normalizeManualDiscount(d);
  if (!n) return null;
  for (const slot of DISKON_SLOTS) {
    const v = n.bySlot[slot];
    if (v == null) continue;
    if (!Number.isFinite(v)) return `Diskon ${labelOf(slot)} bukan angka yang sah.`;
    if (v < 0) return `Diskon ${labelOf(slot)} tidak boleh negatif.`;
  }
  // Tanpa harga tak ada angka untuk dipotong — dan produk Non Web tak punya
  // layar untuk melengkapi harga variannya kelak, jadi diskonnya tak pernah
  // terpakai. Ditolak supaya admin tidak mengira potongannya berlaku.
  if (price == null) return "Isi Harga Jual dulu untuk memakai diskon.";
  for (const slot of DISKON_SLOTS) {
    const v = n.bySlot[slot];
    if (v == null) continue;
    if (n.mode === "persen" && v > MAX_DISKON_PERSEN) {
      return `Diskon ${labelOf(slot)} (persen) harus 0–100.`;
    }
    if (n.mode === "nominal" && v > price) {
      return `Diskon ${labelOf(slot)} (nominal) tidak boleh melebihi Harga Jual.`;
    }
  }
  return null;
}

/**
 * Argumen diskon untuk RPC produk manual (mig 6360). Tanpa diskon → objek
 * kosong: bentuk panggilan lama (7 argumen) dipertahankan, jadi panel Nota &
 * baris impor tanpa diskon tidak bergantung pada parameter baru sama sekali.
 */
export function manualDiscountRpcParams(
  d: ManualDiscount | null | undefined
): Record<string, string | number | null> {
  const n = normalizeManualDiscount(d);
  if (!n) return {};
  const params: Record<string, string | number | null> = { p_diskon_mode: n.mode };
  for (const slot of DISKON_SLOTS) {
    params[`p_${DISKON_SLOT_COLUMN[slot]}`] = n.bySlot[slot] ?? null;
  }
  return params;
}

/** Kolom diskon varian sintetis untuk `getVariantPrice` (pricing.ts). */
export function manualDiscountVariantFields(
  d: ManualDiscount | null | undefined
): Partial<ProductVariation> {
  const n = normalizeManualDiscount(d);
  if (!n) return {};
  const fields: Partial<ProductVariation> = { diskonMode: n.mode };
  for (const slot of DISKON_SLOTS) {
    const v = n.bySlot[slot];
    if (v != null) fields[DISKON_SLOT_KEY[slot]] = v;
  }
  return fields;
}

/** Diskon yang tersimpan di baris `product_variants` (bentuk kanonik). */
export function manualDiscountFromVariantRow(row: {
  diskon_mode?: string | null;
  diskon_reseller?: number | string | null;
  diskon_agen?: number | string | null;
  diskon_slot3?: number | string | null;
  diskon_slot4?: number | string | null;
  diskon_slot5?: number | string | null;
  diskon_slot6?: number | string | null;
  diskon_slot7?: number | string | null;
  diskon_slot8?: number | string | null;
  diskon_slot9?: number | string | null;
  diskon_slot10?: number | string | null;
  diskon_slot11?: number | string | null;
  diskon_slot12?: number | string | null;
  diskon_slot13?: number | string | null;
  diskon_slot14?: number | string | null;
  diskon_slot15?: number | string | null;
}): ManualDiscount | null {
  const bySlot: Partial<Record<DiskonSlot, number>> = {};
  for (const slot of DISKON_SLOTS) {
    const raw = row[DISKON_SLOT_COLUMN[slot]];
    if (raw == null || raw === "") continue;
    const v = Number(raw);
    if (Number.isFinite(v)) bySlot[slot] = v;
  }
  return normalizeManualDiscount({
    mode: row.diskon_mode === "nominal" ? "nominal" : "persen",
    bySlot
  });
}

/**
 * Dua pengaturan sama? Dibandingkan dalam bentuk kanonik — mode hanya
 * bermakna bila ada potongan. Cermin pembanding cabang SKU bersama di RPC
 * upsert (mig 6360).
 */
export function sameManualDiscount(
  a: ManualDiscount | null | undefined,
  b: ManualDiscount | null | undefined
): boolean {
  const x = normalizeManualDiscount(a);
  const y = normalizeManualDiscount(b);
  if (!x || !y) return x === y;
  if (x.mode !== y.mode) return false;
  return DISKON_SLOTS.every((slot) => (x.bySlot[slot] ?? 0) === (y.bySlot[slot] ?? 0));
}

/**
 * Ringkasan satu baris untuk pesan ("persen: Reseller 10, Agen 15"). Tanpa
 * diskon → "tanpa diskon".
 */
export function describeManualDiscount(
  d: ManualDiscount | null | undefined,
  labelOf: (slot: DiskonSlot) => string = (slot) => `slot ${slot}`
): string {
  const n = normalizeManualDiscount(d);
  if (!n) return "tanpa diskon";
  const parts = DISKON_SLOTS.filter((slot) => n.bySlot[slot] != null).map(
    (slot) => `${labelOf(slot)} ${n.bySlot[slot]}`
  );
  return `${n.mode}: ${parts.join(", ")}`;
}
